import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { BLOG_STALE_TIME, blogApi, type BlogLang } from '../../api/blog';
import { ApiError } from '../../api/client';
import { LandingShell, LandingTop, useLandingPrefix } from '../../components/landing/LandingShell';
import { PostCard } from '../../components/landing/PostCard';
import { SignUpLink } from '../../components/landing/SignUpLink';
import { formatDate } from '../../lib/format';
import { initialPost } from '../../lib/initialData';
import { useDocumentHead } from '../../lib/useDocumentHead';
import NotFoundPage from '../NotFoundPage';

/** Below this many sections a table of contents is longer than the scrolling it saves. */
const TOC_MIN_SECTIONS = 3;

/**
 * The section headings, read out of the article's own markup. The server gives every h2 an id
 * when the post is saved (`s-…`), so the table of contents needs no second copy of the structure
 * that could disagree with the text.
 */
export function sectionsOf(bodyHtml: string): { id: string; text: string }[] {
  const sections: { id: string; text: string }[] = [];
  const pattern = /<h2 id="([^"]+)">([\s\S]*?)<\/h2>/g;

  for (const match of bodyHtml.matchAll(pattern)) {
    const text = match[2].replace(/<[^>]+>/g, '').trim();
    if (text) {
      sections.push({ id: match[1], text });
    }
  }

  return sections;
}

export default function BlogPostPage() {
  const { t } = useTranslation();
  const { slug = '' } = useParams();
  const [params] = useSearchParams();
  const prefix = useLandingPrefix();

  const lang: BlogLang = prefix === '/en' ? 'en' : 'bg';
  const locale = lang === 'en' ? 'en-GB' : 'bg-BG';
  const preview = params.get('preview');

  const { data: post, error, isPending } = useQuery({
    queryKey: ['blog-post', lang, slug, preview],
    queryFn: () => blogApi.post(lang, slug, preview),
    // The article the server already wrote into this document, when this is that document. The
    // first render then draws it straight away — no empty frame between the server's markup and
    // ours, and no second request for something already here.
    initialData: () => initialPost(lang, slug),
    staleTime: BLOG_STALE_TIME,
    retry: (count, failure) => !(failure instanceof ApiError && failure.status === 404) && count < 2,
  });

  const otherPrefix = lang === 'en' ? '' : '/en';

  const head = useMemo(() => {
    if (!post) {
      return null;
    }

    const path = `${prefix}/blog/${post.slug}`;

    return {
      title: `${post.metaTitle ?? post.title} — Danak`,
      description: post.metaDescription ?? post.excerpt,
      canonical: path,
      noIndex: post.noIndex,
      image: post.ogImageUrl ?? post.coverImageUrl,
      alternates: post.alternateSlug
        ? [
            { hreflang: lang, href: path },
            { hreflang: lang === 'en' ? 'bg' : 'en', href: `${otherPrefix}/blog/${post.alternateSlug}` },
          ]
        : [],
    };
  }, [post, prefix, otherPrefix, lang]);

  useDocumentHead(head);

  if (error instanceof ApiError && error.status === 404) {
    return <NotFoundPage />;
  }

  if (isPending || !post) {
    // Reached only on client-side navigation; a direct load has the post already. Kept empty on
    // purpose, like the route-level Suspense fallback: a spinner that lives for one frame reads
    // as a flicker.
    return <LandingShell>{error ? <p className="l-lead">{t('common.error')}</p> : null}</LandingShell>;
  }

  const sections = sectionsOf(post.bodyHtml);
  const url = `${window.location.origin}${prefix}/blog/${post.slug}`;
  const share = encodeURIComponent(url);

  return (
    // With a twin, the switcher goes to it. Without one it goes to the other language's index —
    // mirroring the URL would land on /en/blog/<a Bulgarian slug>, which does not exist.
    <LandingShell
      alternatePath={post.alternateSlug ? `${otherPrefix}/blog/${post.alternateSlug}` : `${otherPrefix}/blog`}
    >
      <LandingTop>
        <section className="l-hero l-hero--compact">
          <div>
            <p className="l-eyebrow l-eyebrow--onDark">
              <Link to={`${prefix}/blog`}>{t('blog.eyebrow')}</Link>
              {post.tags.map((tag) => (
                <span key={tag.slug}>
                  {' · '}
                  <Link to={`${prefix}/blog/tag/${tag.slug}`}>{tag.name}</Link>
                </span>
              ))}
            </p>
            <h1>{post.title}</h1>
            <p className="l-hero-sub">
              {post.publishedAt && <time dateTime={post.publishedAt}>{formatDate(post.publishedAt, locale)}</time>}
              {post.publishedAt && ' · '}
              {t('blog.minutes', { count: post.readingMinutes })}
              {' · '}
              {post.authorName}
            </p>
          </div>
        </section>
      </LandingTop>

      <section className="l-section l-article-wrap">
        {post.isPreview && <p className="l-article-note l-article-note--warning">{t('blog.preview')}</p>}

        {post.coverImageUrl && (
          <img className="l-article-cover" src={post.coverImageUrl} alt={post.coverImageAlt ?? ''} />
        )}

        {sections.length >= TOC_MIN_SECTIONS && (
          <nav className="l-toc" aria-label={t('blog.toc')}>
            <h2>{t('blog.toc')}</h2>
            <ol>
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.text}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* The one place in the app that writes stored HTML into the page. It is safe only because
            of what happens on the way in: every body is run through the server's allowlist
            sanitiser when it is saved, and nothing unsanitised is ever stored. If that ever stops
            being true, this line is where it becomes an XSS. */}
        <article className="l-prose l-article" dangerouslySetInnerHTML={{ __html: post.bodyHtml }} />

        {post.faq.length > 0 && (
          <section className="l-prose l-article-faq">
            <h2>{t('blog.faq')}</h2>
            <div className="l-faq">
              {post.faq.map((item) => (
                <details key={item.question}>
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {post.sources.length > 0 && (
          <section className="l-prose">
            <h2>{t('blog.sources')}</h2>
            <ul>
              {post.sources.map((source) => (
                <li key={source.url}>
                  <a href={source.url} target="_blank" rel="noopener noreferrer">
                    {source.title}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <aside className="l-prose l-article-note">
          {post.parameterYear && <p>{t('blog.verifiedAgainst', { year: post.parameterYear })}</p>}
          <p>{t('app.disclaimer')}</p>
          {post.updatedAt && post.publishedAt && post.updatedAt > post.publishedAt && (
            <p>{t('blog.updatedOn', { date: formatDate(post.updatedAt, locale) })}</p>
          )}
          {/* Said plainly, under every generated article. Nobody reads these before they are
              published, and Регламент (ЕС) 2024/1689, чл. 50(4) expects a reader to be told. */}
          {post.aiGenerated && <p>{t('blog.aiDisclosure')}</p>}
        </aside>

        <div className="l-prose l-article-cta">
          <h2>{t('blog.ctaTitle')}</h2>
          <p>{t('blog.ctaBody')}</p>
          <p className="l-article-cta__actions">
            <Link className="l-btn" to={`${prefix}/calculator`}>
              {t('blog.ctaCalculator')}
            </Link>
            <SignUpLink className="l-btn l-btn--ghost">{t('blog.ctaRegister')}</SignUpLink>
          </p>
        </div>

        <p className="l-prose l-share">
          {t('blog.share')}:{' '}
          {/* Plain share URLs. The networks' own buttons are scripts that track every reader of
              every article whether or not they ever press one. */}
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${share}`} target="_blank" rel="noopener noreferrer">
            LinkedIn
          </a>
          {' · '}
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${share}`} target="_blank" rel="noopener noreferrer">
            Facebook
          </a>
          {' · '}
          <a href={`https://x.com/intent/post?url=${share}&text=${encodeURIComponent(post.title)}`} target="_blank" rel="noopener noreferrer">
            X
          </a>
        </p>
      </section>

      {post.related.length > 0 && (
        <section className="l-section l-related">
          <h2>{t('blog.related')}</h2>
          <div className="l-post-grid">
            {post.related.map((related) => (
              <PostCard key={related.slug} post={related} prefix={prefix} locale={locale} />
            ))}
          </div>
        </section>
      )}
    </LandingShell>
  );
}
